// library — the per-subdomain client module for the Library (Bibliotek): a
// searchable register the author walks INTO. Implements the module contract
// (modules/module.ts); talks to other modules only through Signals.
//
// The query text and the opened row are frame state (the active Search tab,
// URL-mirrored): the module's intents request frame transitions
// (`Signal.FrameRequested`), and the frame's state comes back as `shown`.
//
// Topology (entry file + sibling folder):
//  - library/model.ts      — LibraryModel (marked row, browse order, the
//    content search and the opened item's content as Remote reads)
//  - library/msg.ts        — LibraryMsg (frame facts, intents, outcomes)
//  - library/effect.ts     — LibraryEffect (port calls as data)
//  - library/update.ts     — updateLibrary + inboundLibrary (pure)
//  - library/view-model.ts — the list as shown: ranking, tabs, order (pure)
//  - library/view.tsx      — LibraryView (pure React projection)
//  - library/run.ts        — the interpreter (the ONE impure file)

import type { LibrarySort } from "../components/Search/searchModel.js";
import type { LibraryEffect } from "./library/effect.js";
import { initialLibraryModel, type LibraryModel } from "./library/model.js";
import type { LibraryMsg } from "./library/msg.js";
import { type LibraryDeps, mkRunLibrary } from "./library/run.js";
import {
    inboundLibrary,
    type LibraryRefusal,
    updateLibrary,
} from "./library/update.js";
import type { Module } from "./module.js";

export type { ItemContent, LibraryModel } from "./library/model.js";
export type { LibraryMsg } from "./library/msg.js";
export type { LibraryDeps } from "./library/run.js";

export type LibraryModule = Module<
    LibraryModel,
    LibraryMsg,
    LibraryEffect,
    LibraryRefusal
>;

// The module, its interpreter bound to the ports, starting in the browse
// order this device remembers.
export const mkLibraryModule = (
    deps: LibraryDeps,
    sort: LibrarySort,
): LibraryModule => ({
    init: initialLibraryModel(sort),
    update: updateLibrary,
    run: mkRunLibrary(deps),
    inbound: inboundLibrary,
});
